import { v4 as uuidv4 } from 'uuid';

export const LogLevel = {
  DEBUG: 'DEBUG',
  INFO: 'INFO',
  WARN: 'WARN',
  ERROR: 'ERROR',
  FATAL: 'FATAL'
};

const LogColors = {
  DEBUG: 'color: #888; font-weight: bold;',
  INFO: 'color: #007bff; font-weight: bold;',
  WARN: 'color: #ffc107; font-weight: bold;',
  ERROR: 'color: #dc3545; font-weight: bold;',
  FATAL: 'color: #721c24; font-weight: bold; background: #f8d7da;'
};

class Logger {
  constructor(serviceName = 'frontend-app') {
    this.serviceName = serviceName;
    this.sessionId = uuidv4();
    this.userId = this.getUserId();
    this.environment = process.env.NODE_ENV || 'development';
    this.isDevelopment = this.environment === 'development';
    this.isProduction = this.environment === 'production';
  }

  getUserId() {
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      return userData?.id || 'anonymous';
    } catch {
      return 'anonymous';
    }
  }

  createLogEntry(level, message, meta = {}) {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level,
      service: this.serviceName,
      sessionId: this.sessionId,
      userId: this.userId,
      environment: this.environment,
      message: String(message),
      ...meta,
      browser: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        url: window.location.href,
        path: window.location.pathname,
        referrer: document.referrer
      },
      performance: {
        memory: window.performance?.memory ? {
          usedJSHeapSize: Math.round(window.performance.memory.usedJSHeapSize / 1024 / 1024),
          totalJSHeapSize: Math.round(window.performance.memory.totalJSHeapSize / 1024 / 1024)
        } : null,
        timing: window.performance?.timing ? {
          loadTime: window.performance.timing.loadEventEnd - window.performance.timing.navigationStart
        } : null
      }
    };

    if (level === LogLevel.ERROR || level === LogLevel.FATAL) {
      logEntry.stackTrace = new Error().stack;
    }

    return logEntry;
  }

  formatForConsole(logEntry) {
    const { timestamp, level, message, component, ...rest } = logEntry;
    const time = new Date(timestamp).toLocaleTimeString();
    const prefix = component ? `[${component}]` : '';

    return {
      formatted: `%c[${time}] [${level}]${prefix} ${message}`,
      style: LogColors[level] || LogColors.INFO,
      data: rest
    };
  }

  async sendToBackend(logEntry) {
    if (!this.isProduction) return;

    try {
      await fetch('/api/logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': this.sessionId
        },
        body: JSON.stringify(logEntry),
        keepalive: true
      });
    } catch (error) {
      this.storeLogLocally(logEntry);
    }
  }

  storeLogLocally(logEntry) {
    try {
      const storedLogs = JSON.parse(localStorage.getItem('pending_logs') || '[]');
      storedLogs.push({
        ...logEntry,
        storedAt: new Date().toISOString()
      });

      if (storedLogs.length > 100) {
        storedLogs.splice(0, storedLogs.length - 100);
      }

      localStorage.setItem('pending_logs', JSON.stringify(storedLogs));
    } catch (error) {
      console.error('Failed to store log locally:', error);
    }
  }

  async flushStoredLogs() {
    try {
      const storedLogs = JSON.parse(localStorage.getItem('pending_logs') || '[]');
      if (storedLogs.length === 0) return;

      await fetch('/api/logs/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ logs: storedLogs })
      });

      localStorage.removeItem('pending_logs');
    } catch (error) {
      console.error('Failed to flush stored logs:', error);
    }
  }

  log(level, message, meta = {}) {
    const logEntry = this.createLogEntry(level, message, meta);

    if (this.isDevelopment) {
      const { formatted, style, data } = this.formatForConsole(logEntry);
      console.log(formatted, style, data);
    }

    if (this.isProduction) {
      this.sendToBackend(logEntry);
    }

    window.dispatchEvent(new CustomEvent('app:log', { detail: logEntry }));

    return logEntry;
  }

  debug(message, meta = {}) {
    return this.log(LogLevel.DEBUG, message, meta);
  }

  info(message, meta = {}) {
    return this.log(LogLevel.INFO, message, meta);
  }

  warn(message, meta = {}) {
    return this.log(LogLevel.WARN, message, meta);
  }

  error(message, meta = {}) {
    return this.log(LogLevel.ERROR, message, meta);
  }

  fatal(message, meta = {}) {
    return this.log(LogLevel.FATAL, message, meta);
  }

  createComponentLogger(componentName) {
    return {
      debug: (message, meta = {}) =>
        this.debug(message, { ...meta, component: componentName }),
      info: (message, meta = {}) =>
        this.info(message, { ...meta, component: componentName }),
      warn: (message, meta = {}) =>
        this.warn(message, { ...meta, component: componentName }),
      error: (message, meta = {}) =>
        this.error(message, { ...meta, component: componentName }),
      fatal: (message, meta = {}) =>
        this.fatal(message, { ...meta, component: componentName })
    };
  }
}

const loggerInstance = new Logger('social-app-frontend');

export const logger = loggerInstance;
export default loggerInstance;

export const useLogger = (componentName) => {
  return loggerInstance.createComponentLogger(componentName);
};